import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { DelegationPerson, DelegationsService, POWER } from 'src/app/service/delegations.service';
import { TaskComposerService, TaskPrefill } from '../../services/task-composer.service';
import { ProofKind, ProofKindDef, RepeatFreq, TaskDepartment, TaskFile, TaskLink, TaskPersonOption, TaskTypeDef, TaskTypes, TasksService, TASK_BACKDATE_MAX_DAYS, canBackdateTasks, detectTaskType, taskDueLabel, taskTimeLabel, taskTodayYMD } from '../../services/tasks.service';
import { DuePickerComponent } from '../due-picker/due-picker.component';
import { PeoplePickerComponent, PickTarget } from '../people-picker/people-picker.component';
import { TaskAvatarComponent } from '../task-avatar/task-avatar.component';

/**
 * The one "New task" window, mounted once in AppComponent and opened from
 * anywhere through TaskComposerService. Title + who + when is all it needs;
 * details, files and a link are there when wanted.
 */
@Component({
  selector: 'app-task-composer',
  standalone: true,
  imports: [CommonModule, FormsModule, PeoplePickerComponent, DuePickerComponent, TaskAvatarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './task-composer.component.html',
  styleUrls: ['./task-composer.component.scss'],
})
export class TaskComposerComponent implements OnInit, OnDestroy {
  private readonly composer = inject(TaskComposerService);
  private readonly tasks = inject(TasksService);
  private readonly toast = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly delegations = inject(DelegationsService);
  private sub?: Subscription;
  /** Directors this user may give tasks for; empty for almost everyone. */
  actsFor: DelegationPerson[] = [];
  onBehalfOf: DelegationPerson | null = null;
  fromOpen = false;
  /** Repeating: none, or a frequency with weekdays (weekly) and an optional last date. */
  repeatFreq: RepeatFreq | '' = '';
  repeatDays: number[] = [];
  repeatEnds: string | null = null;
  repeatOpen = false;
  readonly dow = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  readonly dowFull = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  @ViewChild('titleEl') titleEl?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileEl') fileEl?: ElementRef<HTMLInputElement>;
  @ViewChild('typeSearch') typeSearch?: ElementRef<HTMLInputElement>;

  /** Kind of work. Tagged from the title until the person picks one (typeAuto). */
  catalogue: TaskTypes | null = null;
  type = 'general';
  typeAuto = true;
  typeOpen = false;
  typeQuery = '';
  /** Proof needed when done. Follows the type (and whether the task is for yourself) until picked. */
  /** Every proof kind the task asks for (empty = none). */
  proofKinds: ProofKind[] = [];
  proofNote = '';
  proofAuto = true;
  proofOpen = false;
  dueTime: string | null = null;
  myDepartment = (localStorage.getItem('department') || '').toUpperCase();

  open = false;
  saving = false;
  uploading = false;
  error = '';
  showDetails = false;

  title = '';
  details = '';
  priority: 'normal' | 'high' = 'normal';
  dueDate: string | null = taskTodayYMD();
  /** Admins only, one week from 20 Oct 2026: the day the work started, when earlier than today (back-dates the task). */
  startedOn: string | null = null;
  /** Shown only when the browser has no showPicker(); otherwise the chip opens the native picker directly. */
  startPickerInline = false;
  /** Read each time (the composer is mounted once, before login; the day can roll over in an open tab). */
  get canBackdate(): boolean {
    return canBackdateTasks();
  }
  get todayYMD(): string {
    return taskTodayYMD();
  }
  get startMinYMD(): string {
    return taskTodayYMD(-TASK_BACKDATE_MAX_DAYS);
  }
  /** Who gets it: empty = me; one person or one queue = a normal task; more = one task each under a group parent. */
  targets: Exclude<PickTarget, { kind: 'me' }>[] = [];
  link: TaskLink | null = null;
  files: TaskFile[] = [];
  source = 'manual';
  sourceRef: Record<string, any> | undefined;
  parentTaskId?: string;
  parentTitle?: string;
  myName = localStorage.getItem('username') || 'me';

  ngOnInit() {
    this.sub = this.composer.open$.subscribe((p) => void this.show(p));
  }
  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  private async show(p: TaskPrefill) {
    this.reset();
    try {
      await this.delegations.loadMe();
      this.actsFor = this.delegations.actsFor(POWER.TASKS_ASSIGN_ON_BEHALF);
    } catch {
      this.actsFor = [];
    }
    try {
      this.catalogue = await this.tasks.types();
    } catch {
      this.catalogue = null;
    }
    this.title = p.title || '';
    this.details = p.details || '';
    this.showDetails = !!this.details;
    this.priority = p.priority || 'normal';
    this.link = p.link || null;
    this.source = p.source || 'manual';
    this.sourceRef = p.sourceRef;
    this.parentTaskId = p.parentTaskId;
    this.parentTitle = p.parentTitle;
    if (p.assigneeId) {
      const me = localStorage.getItem('guestID') || '';
      if (p.assigneeId === me) this.targets = [];
      else {
        const person = (await this.tasks.people()).find((x) => x.userId === p.assigneeId);
        if (person) this.targets = [{ kind: 'person', person }];
      }
    } else if (p.department) {
      const dept = (await this.tasks.departments()).find((d) => d.name === p.department.toUpperCase());
      if (dept) this.targets = [{ kind: 'department', department: dept }];
    }
    this.dueDate = p.dueDate !== undefined ? p.dueDate : this.isMe ? taskTodayYMD(0) : taskTodayYMD(2);
    if (p.type && this.catalogue?.types.some((t) => t.id === p.type)) {
      this.type = p.type;
      this.typeAuto = false;
    } else this.retag();
    this.reproof();
    this.open = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      const el = this.titleEl?.nativeElement;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
        this.autosize(el);
      }
    }, 30);
  }

  private reset() {
    this.saving = false;
    this.uploading = false;
    this.error = '';
    this.showDetails = false;
    this.title = '';
    this.details = '';
    this.priority = 'normal';
    this.dueDate = taskTodayYMD();
    this.startedOn = null;
    this.startPickerInline = false;
    this.targets = [];
    this.link = null;
    this.files = [];
    this.source = 'manual';
    this.sourceRef = undefined;
    this.parentTaskId = undefined;
    this.parentTitle = undefined;
    this.onBehalfOf = null;
    this.fromOpen = false;
    this.repeatFreq = '';
    this.repeatDays = [];
    this.repeatEnds = null;
    this.repeatOpen = false;
    this.type = 'general';
    this.typeAuto = true;
    this.typeOpen = false;
    this.typeQuery = '';
    this.proofKinds = [];
    this.proofNote = '';
    this.proofAuto = true;
    this.proofOpen = false;
    this.dueTime = null;
  }

  // ── type and proof ─────────────────────────────────────────────────────────
  get typeDef(): TaskTypeDef | undefined {
    return this.catalogue?.types.find((t) => t.id === this.type);
  }
  get proofShort(): string {
    return this.proofKinds.map((k) => this.proofDefs.find((d) => d.id === k)?.short || k).join(' + ');
  }
  get proofIcon(): string {
    return this.proofDefs.find((d) => d.id === this.proofKinds[0])?.icon || 'attach_file';
  }
  /** The proof kinds a person can pick (everything but 'none', which is "clear"). */
  get proofDefs(): ProofKindDef[] {
    return (this.catalogue?.proofKinds || []).filter((k) => k.id !== 'none');
  }
  hasProof(k: ProofKind): boolean {
    return this.proofKinds.includes(k);
  }
  // ── who ──
  get isMe(): boolean {
    return this.targets.length === 0;
  }
  get isGroup(): boolean {
    return this.targets.length > 1 || this.targets.some((t) => t.kind === 'department' && !!t.everyone);
  }
  get chosenKeys(): string[] {
    return this.targets.map((t) => (t.kind === 'person' ? t.person.userId : `${t.everyone ? 'all:' : 'queue:'}${t.department.name}`));
  }
  targetName(t: Exclude<PickTarget, { kind: 'me' }>): string {
    return t.kind === 'person' ? t.person.name : t.everyone ? `Everyone in ${t.department.name} (${t.department.members})` : `${t.department.name} queue`;
  }
  removeTarget(i: number) {
    this.targets = this.targets.filter((_, idx) => idx !== i);
    this.reproof();
    this.cdr.markForCheck();
  }
  get timeLabel(): string {
    return taskTimeLabel(this.dueTime);
  }
  /** Types the person's department uses most, the detected one first. */
  get suggestedTypes(): TaskTypeDef[] {
    const all = this.catalogue?.types || [];
    const first = this.targets[0];
    const dept = first?.kind === 'department' ? first.department.name.toUpperCase() : first?.kind === 'person' ? (first.person.department || '').toUpperCase() : this.myDepartment;
    const common = all.filter((t) => t.depts.includes(dept)).slice(0, 6);
    const detected = this.typeDef && this.type !== 'general' ? [this.typeDef] : [];
    const out: TaskTypeDef[] = [];
    for (const t of [...detected, ...common]) if (!out.some((x) => x.id === t.id)) out.push(t);
    return out.slice(0, 7);
  }
  /** The catalogue by group, narrowed by the search box. */
  get typeGroups(): { id: string; label: string; types: TaskTypeDef[] }[] {
    const c = this.catalogue;
    if (!c) return [];
    const q = this.typeQuery.trim().toLowerCase();
    return c.groups
      .map((g) => ({ id: g.id, label: g.label, types: c.types.filter((t) => t.group === g.id && (!q || t.label.toLowerCase().includes(q) || t.keywords.some((k) => k.trim().startsWith(q)))) }))
      .filter((g) => g.types.length);
  }
  /** Tag the task from its title while the person has not chosen a type. */
  retag() {
    if (!this.typeAuto || !this.catalogue) return;
    const next = detectTaskType(this.title, this.catalogue.types);
    if (next !== this.type) {
      this.type = next;
      this.reproof();
    }
  }
  /** The type's usual proof for a task given to someone else; none for your own, until the person picks. */
  reproof() {
    if (!this.proofAuto) return;
    const usual = this.typeDef?.proof;
    this.proofKinds = this.isMe || !usual || usual === 'none' ? [] : [usual];
  }
  toggleType() {
    this.typeOpen = !this.typeOpen;
    this.proofOpen = false;
    this.repeatOpen = false;
    this.fromOpen = false;
    if (this.typeOpen) setTimeout(() => this.typeSearch?.nativeElement.focus(), 30);
    this.cdr.markForCheck();
  }
  pickType(id: string) {
    this.type = id;
    this.typeAuto = false;
    this.typeOpen = false;
    this.typeQuery = '';
    this.reproof();
    this.cdr.markForCheck();
  }
  toggleProof() {
    this.proofOpen = !this.proofOpen;
    this.typeOpen = false;
    this.repeatOpen = false;
    this.fromOpen = false;
    this.cdr.markForCheck();
  }
  /** Toggle one kind; 'none' clears them all. More than one kind means all of them are needed. */
  pickProof(kind: ProofKind) {
    this.proofAuto = false;
    if (kind === 'none') this.proofKinds = [];
    else this.proofKinds = this.proofKinds.includes(kind) ? this.proofKinds.filter((k) => k !== kind) : [...this.proofKinds.filter((k) => k !== 'any'), kind];
    if (!this.proofKinds.length) this.proofNote = '';
    this.cdr.markForCheck();
  }

  setRepeat(f: RepeatFreq | '') {
    this.repeatFreq = f;
    if (f === 'weekly' && !this.repeatDays.length) this.repeatDays = [this.dueWeekday];
    if (f !== 'weekly') this.repeatDays = [];
    if (!f) this.repeatEnds = null;
    this.cdr.markForCheck();
  }
  toggleDay(d: number) {
    this.repeatDays = this.repeatDays.includes(d) ? this.repeatDays.filter((x) => x !== d) : [...this.repeatDays, d].sort();
    if (!this.repeatDays.length) this.repeatDays = [this.dueWeekday];
  }
  get dueWeekday(): number {
    const d = this.dueDate || taskTodayYMD();
    const [y, m, dd] = d.split('-').map(Number);
    return new Date(y, m - 1, dd).getDay();
  }
  get dueDay(): number {
    return Number((this.dueDate || taskTodayYMD()).split('-')[2]);
  }
  get repeatLabel(): string {
    switch (this.repeatFreq) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return `Weekly · ${this.repeatDays.map((d) => this.dowFull[d]).join(', ')}`;
      case 'monthly':
        return `Monthly · ${this.dueDay}`;
      case 'quarterly':
        return `Quarterly · ${this.dueDay}`;
      default:
        return 'Never';
    }
  }

  pickFrom(p: DelegationPerson | null) {
    this.onBehalfOf = p;
    this.fromOpen = false;
    this.cdr.markForCheck();
  }

  close() {
    if (this.saving) return;
    this.open = false;
    this.cdr.markForCheck();
  }

  @HostListener('document:click')
  onDocClick() {
    if (this.repeatOpen || this.fromOpen || this.typeOpen || this.proofOpen) {
      this.repeatOpen = false;
      this.fromOpen = false;
      this.typeOpen = false;
      this.proofOpen = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKey(ev: KeyboardEvent) {
    if (!this.open) return;
    if (ev.key === 'Escape') {
      ev.preventDefault();
      if (this.repeatOpen || this.fromOpen || this.typeOpen || this.proofOpen) {
        this.repeatOpen = false;
        this.fromOpen = false;
        this.typeOpen = false;
        this.proofOpen = false;
        this.cdr.markForCheck();
        return;
      }
      this.close();
    } else if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) {
      ev.preventDefault();
      void this.save();
    }
  }

  onTitleKey(ev: KeyboardEvent) {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      void this.save();
    }
  }

  autosize(el: HTMLTextAreaElement) {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    this.retag();
  }

  onPick(t: PickTarget) {
    const wasMe = this.isMe;
    if (t.kind === 'me') this.targets = [];
    else {
      const key = t.kind === 'person' ? t.person.userId : `${t.everyone ? 'all:' : 'queue:'}${t.department.name}`;
      const i = this.chosenKeys.indexOf(key);
      if (i >= 0) this.targets = this.targets.filter((_, idx) => idx !== i); // picking again removes
      else if (t.kind === 'department') this.targets = [...this.targets.filter((x) => !(x.kind === 'department' && x.department.name === t.department.name)), t]; // queue and everyone of one department do not mix
      else this.targets = [...this.targets, t];
    }
    // A task for someone else defaults to two days out; one for yourself to today.
    if (!this.isMe && wasMe && this.dueDate === taskTodayYMD(0)) this.dueDate = taskTodayYMD(2);
    if (this.isMe && !wasMe && this.dueDate === taskTodayYMD(2)) this.dueDate = taskTodayYMD(0);
    this.reproof();
    this.cdr.markForCheck();
  }

  get targetLabel(): string {
    if (this.isMe) return 'Me';
    if (this.targets.length === 1) return this.targetName(this.targets[0]);
    const names = this.targets.map((t) => (t.kind === 'person' ? t.person.name.split(' ')[0] : t.everyone ? `all ${t.department.name}` : `${t.department.name} queue`));
    return names.length > 3 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ');
  }
  get targetPerson(): TaskPersonOption | null {
    return this.targets.length === 1 && this.targets[0].kind === 'person' ? this.targets[0].person : null;
  }
  get targetDept(): TaskDepartment | null {
    return this.targets.length === 1 && this.targets[0].kind === 'department' ? this.targets[0].department : null;
  }
  get dueLabel(): string {
    return taskDueLabel(this.dueDate);
  }
  /** The Started chip opens the browser's own date picker; browsers without showPicker() get the field inline. */
  openStartPicker(el: HTMLInputElement) {
    const picker = el as HTMLInputElement & { showPicker?: () => void };
    try {
      if (typeof picker.showPicker === 'function') {
        picker.showPicker();
        return;
      }
    } catch {
      /* fall through to the inline field */
    }
    this.startPickerInline = true;
    setTimeout(() => el.focus(), 0);
  }
  get startLabel(): string {
    return this.startedOn && this.startedOn < this.todayYMD ? taskDueLabel(this.startedOn) : 'Today';
  }
  get dueClass(): string {
    if (!this.dueDate) return 'none';
    const today = taskTodayYMD(0);
    return this.dueDate < today ? 'overdue' : this.dueDate === today ? 'today' : 'later';
  }

  togglePriority() {
    this.priority = this.priority === 'high' ? 'normal' : 'high';
  }

  async onFiles(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const list = Array.from(input.files || []);
    input.value = '';
    if (!list.length) return;
    this.uploading = true;
    this.error = '';
    this.cdr.markForCheck();
    try {
      for (const f of list) this.files = [...this.files, await this.tasks.upload(f)];
    } catch (e) {
      this.error = TasksService.errorText(e, 'That file could not be uploaded');
    } finally {
      this.uploading = false;
      this.cdr.markForCheck();
    }
  }
  removeFile(i: number) {
    this.files = this.files.filter((_, idx) => idx !== i);
  }

  async save() {
    if (this.saving) return;
    const title = this.title.trim();
    if (!title) {
      this.error = 'Give the task a title';
      this.titleEl?.nativeElement.focus();
      this.cdr.markForCheck();
      return;
    }
    this.saving = true;
    this.error = '';
    this.cdr.markForCheck();
    try {
      const t = await this.tasks.create({
        title,
        details: this.details.trim(),
        priority: this.priority,
        dueDate: this.dueDate,
        dueTime: this.dueDate ? this.dueTime : null,
        startedOn: this.canBackdate && this.startedOn && this.startedOn < this.todayYMD ? this.startedOn : undefined,
        type: this.type,
        proof: this.proofKinds.length ? { kinds: this.proofKinds, note: this.proofNote.trim() } : null,
        link: this.link,
        files: this.files,
        source: this.source,
        sourceRef: this.sourceRef,
        parentTaskId: this.parentTaskId,
        onBehalfOf: this.onBehalfOf?.userId,
        repeat: this.repeatFreq ? { freq: this.repeatFreq, days: this.repeatFreq === 'weekly' ? this.repeatDays : undefined, dayOfMonth: this.repeatFreq === 'monthly' || this.repeatFreq === 'quarterly' ? this.dueDay : undefined, endsOn: this.repeatEnds } : undefined,
        ...(this.isGroup
          ? { targets: this.targets.map((x) => (x.kind === 'person' ? { userId: x.person.userId } : { department: x.department.name, everyone: !!x.everyone })) }
          : this.targets[0]?.kind === 'person'
            ? { assigneeId: this.targets[0].person.userId }
            : this.targets[0]?.kind === 'department'
              ? { department: this.targets[0].department.name }
              : {}),
      });
      this.composer.created$.next(t as any);
      this.open = false;
      const who = this.isMe ? 'for you' : this.isGroup ? `for ${(t as any)?.childStats?.total || this.targets.length} people` : this.targets[0].kind === 'person' ? `for ${this.targets[0].person.name}` : `in the ${this.targets[0].department.name} queue`;
      this.toast.success(`Task created ${who}${this.onBehalfOf ? ` in ${this.onBehalfOf.name}'s name` : ''}`);
      if (!this.router.url.startsWith('/tasks')) {
        // Nothing else to do here; the Tasks badge updates itself.
      }
    } catch (e) {
      this.error = TasksService.errorText(e, 'The task could not be created');
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  openTasks() {
    this.close();
    void this.router.navigate(['/tasks']);
  }
}
